import { Search, X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { z } from "zod";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDateRangeField } from "@/shared/components/forms/ColocaDateRangeField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaSelectField } from "@/shared/components/forms/ColocaSelectField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { useTranslations } from "@/shared/lang/useTranslations";

import {
  ORGANIZATION_WEBHOOK_DELIVERY_STATUSES,
  type OrganizationWebhookEventCatalogEntry,
} from "../../services/organizationWebhooksService";
import {
  DEFAULT_DELIVERIES_FILTERS,
  eventsForType,
  isEventCompatibleWithType,
  type DeliveriesFilterState,
} from "../../utils/organizationWebhooks.utils";
import { deliveryStatusLabelKey, eventLabel, eventTypeLabel } from "./webhookLabels";

export interface WebhookDeliveriesFiltersProps {
  /** The filters currently APPLIED to the report — the form is seeded from them and diffed against them. */
  filters: DeliveriesFilterState;
  eventTypes: OrganizationWebhookEventCatalogEntry[];
  hasActiveFilters: boolean;
  /** A page is in flight — the apply button holds its spinner until the table has the new rows. */
  isApplying: boolean;
  onChange: (next: DeliveriesFilterState) => void;
  onClear: () => void;
}

const filtersSchema = z.object({
  status: z.string(),
  type: z.string(),
  event: z.string(),
  status_code: z.string(),
  from: z.string(),
  to: z.string(),
});

/**
 * Server-side filters of the report, applied on submit rather than on every keystroke.
 *
 * Live-applying meant a request per character of the HTTP code and a reload on each half-picked date range,
 * with the table flickering under the reader. Here the form is a draft: it is diffed against the applied
 * filters, the button only lights up when something would actually change, and the request goes out once.
 */
export function WebhookDeliveriesFilters({
  filters,
  eventTypes,
  hasActiveFilters,
  isApplying,
  onChange,
  onClear,
}: WebhookDeliveriesFiltersProps) {
  const { t } = useTranslations("settings");
  const form = useZodForm(filtersSchema, filters);

  // Re-seed when the applied filters change from outside (a clear, or a reset after an error), so the draft
  // never claims a filter the report is not using.
  useEffect(() => {
    form.reset(filters);
  }, [filters, form]);

  const draft = form.watch() as DeliveriesFilterState;

  // An event that no longer belongs to the chosen type is a pair the backend never produces, so it is
  // corrected in the draft itself instead of being sent and returning nothing.
  const selectedType = draft.type;
  useEffect(() => {
    if (!isEventCompatibleWithType(eventTypes, selectedType, form.getValues("event"))) {
      form.setValue("event", "all");
    }
  }, [selectedType, eventTypes, form]);

  const hasPendingChanges =
    draft.status !== filters.status ||
    draft.type !== filters.type ||
    draft.event !== filters.event ||
    draft.status_code !== filters.status_code ||
    draft.from !== filters.from ||
    draft.to !== filters.to;

  const statusItems = useMemo(
    () => [
      { value: "all", label: t("webhooks_filter_all") },
      ...ORGANIZATION_WEBHOOK_DELIVERY_STATUSES.map((status) => ({
        value: status,
        label: t(deliveryStatusLabelKey(status)),
      })),
    ],
    [t],
  );

  const typeItems = useMemo(
    () => [
      { value: "all", label: t("webhooks_filter_all") },
      ...eventTypes.map((entry) => ({ value: entry.type, label: eventTypeLabel(t, entry.type) })),
    ],
    [eventTypes, t],
  );

  const allEventsItem = useMemo(() => [{ value: "all", label: t("webhooks_filter_all") }], [t]);

  // With a type chosen, the list is that group's events. With "all", every event is offered, grouped under
  // its type's label — thirty-odd flat options would be unreadable, and the grouping says which filter the
  // reader could narrow by instead.
  const eventItems = useMemo(
    () =>
      selectedType === "all"
        ? allEventsItem
        : [
            ...allEventsItem,
            ...eventsForType(eventTypes, selectedType).map((event) => ({
              value: event,
              label: eventLabel(t, event),
            })),
          ],
    [allEventsItem, eventTypes, selectedType, t],
  );

  const eventGroups = useMemo(
    () =>
      selectedType === "all"
        ? eventTypes
            .filter((entry) => entry.events.length > 0)
            .map((entry) => ({
              label: eventTypeLabel(t, entry.type),
              items: entry.events.map((event) => ({ value: event, label: eventLabel(t, event) })),
            }))
        : undefined,
    [eventTypes, selectedType, t],
  );

  return (
    <ColocaForm
      form={form}
      onSubmit={(values) => onChange(values as DeliveriesFilterState)}
      className="flex flex-col gap-2 space-y-0 sm:flex-row sm:flex-wrap sm:items-end"
    >
      <div className="w-full sm:w-[150px]">
        <ColocaSelectField name="status" label={t("webhooks_filter_status")} items={statusItems} size="sm" />
      </div>
      <div className="w-full sm:w-[130px]">
        <ColocaSelectField name="type" label={t("webhooks_filter_type")} items={typeItems} size="sm" />
      </div>
      <div className="w-full sm:w-[230px]">
        <ColocaSelectField
          name="event"
          label={t("webhooks_filter_event")}
          items={eventItems}
          groups={eventGroups}
          size="sm"
        />
      </div>
      <div className="w-full sm:w-[110px]">
        <ColocaInputField
          name="status_code"
          label={t("webhooks_filter_status_code")}
          placeholder="503"
          inputMode="numeric"
          maxLength={3}
          transform={(raw) => raw.replace(/\D/g, "").slice(0, 3)}
        />
      </div>
      <div className="w-full sm:w-[240px]">
        <ColocaDateRangeField
          nameFrom="from"
          nameTo="to"
          label={t("webhooks_filter_range")}
          maxDate={new Date()}
        />
      </div>
      {/* Stacked below `sm` for the same reason as every other action row on this page: a `ColocaButton` is
          `shrink-0 whitespace-nowrap`, so a row of them is not bounded by its container's width. */}
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
        <ColocaButton
          type="submit"
          variant="primary"
          size="sm"
          icon={Search}
          loading={isApplying}
          disabled={!hasPendingChanges}
          className="w-full sm:w-auto"
        >
          {t("webhooks_filter_apply")}
        </ColocaButton>
        {(hasActiveFilters || hasPendingChanges) && (
          <ColocaButton
            type="button"
            variant="ghost"
            size="sm"
            icon={X}
            disabled={isApplying}
            onClick={() => {
              form.reset(DEFAULT_DELIVERIES_FILTERS);
              onClear();
            }}
            className="w-full sm:w-auto"
          >
            {t("webhooks_filter_clear")}
          </ColocaButton>
        )}
      </div>
    </ColocaForm>
  );
}
